'use client';

import {
  FileUploadResponse,
  HelpFormItemType,
} from '@dev-landing/forms/Forms.entity';
import CancelIcon from '@dev-landing/img/icons/cancel.svg';
import FileIcon from '@dev-landing/img/icons/file.svg';
import UploadImage from '@dev-landing/img/icons/upload.svg';
import {keeperFetchResponse} from '@mail-core/dashboard/keeper/binding/fetch';
import {validateFile} from '@utils/forms/validateFile';
import type {ChangeEvent} from 'react';
import {useRef, useState} from 'react';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';

import {
  acceptedFileUploadFilesTypes,
  MAX_FILE_AMOUNT,
  MAX_FILE_SIZE,
} from './FileUpload.constants';
import styles from './FileUpload.module.scss';

type Props = {
  title: string;
  fileExtensions: string;
  fileUploadApiUrl: string;
};

export const FileUpload = ({
  title,
  fileExtensions = '',
  fileUploadApiUrl,
}: Props) => {
  const {control, watch, setError} = useFormContext<HelpFormItemType>();

  const fieldsArray = useFieldArray<HelpFormItemType, 'fileUpload'>({
    control,
    name: 'fileUpload',
  });

  const fields = watch('fileUpload');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isAppendFileAvailable =
    !isLoading &&
    fields?.length < MAX_FILE_AMOUNT &&
    fields?.every((item) => item?.url?.length);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];

    if (!file) return null;

    if (inputRef.current) {
      inputRef.current.value = '';
    }

    const fieldName =
      `fileUpload.${fieldsArray.fields.length}` as `fileUpload.${number}`;

    fieldsArray.append({name: file.name, url: ''});

    const {isValidExtension, isValidSize} = validateFile({
      file,
      maxFileSize: MAX_FILE_SIZE,
    });

    const isInvalidFile =
      !isValidExtension ||
      !isValidSize ||
      fields.some((_file) => _file.name === file.name);

    if (isInvalidFile) {
      setError(fieldName, {
        message: 'Некорректный формат файла',
      });
    } else {
      setIsLoading((state) => !state);

      try {
        const formData = new FormData();
        formData.append('image', file);

        const result: FileUploadResponse = await keeperFetchResponse(
          fetch(fileUploadApiUrl, {
            method: 'POST',
            body: formData,
          }),
          'json',
        );

        fieldsArray.update(fieldsArray.fields.length, {
          name: file.name,
          url: result.fileName,
        });
      } catch (e) {
        setError(fieldName, {
          message: 'Произошла ошибка при загрузке файла',
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    fieldsArray.remove(index);
  };

  return (
    <div className={styles.root}>
      <div className={styles.files}>
        {fieldsArray.fields.map((item, index) => (
          <Controller
            key={item.id}
            name={`fileUpload.${index}`}
            control={control}
            render={({field, fieldState}) => (
              <>
                <div className={styles.selectedFile}>
                  <img
                    src={FileIcon.src}
                    alt="fileIcon"
                    className={styles.fileIcon}
                  />
                  <p>{field.value.name}</p>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className={styles.deleteButton}
                    disabled={isLoading}
                    data-testid={'fileupload-remove-button-id'}
                  >
                    <img
                      src={CancelIcon.src}
                      alt="cancelIcon"
                      className={styles.fileIcon}
                    />
                  </button>
                </div>
                <span className={styles.errorMessage}>
                  {fieldState.error?.message}
                </span>
              </>
            )}
          />
        ))}
      </div>

      {isAppendFileAvailable && (
        <label htmlFor="upload" className={styles.label}>
          <img src={UploadImage.src} alt="upload image" />
          {title}
          <span className={styles.extensions}>{fileExtensions}</span>
        </label>
      )}

      <input
        type="file"
        id="upload"
        ref={inputRef}
        className={styles.input}
        onChange={handleFileChange}
        accept={acceptedFileUploadFilesTypes}
        data-testid={'file_upload_id'}
      />
    </div>
  );
};
